import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type {User} from 'firebase/auth';
import {auth} from '../firebase';
import {
  waitForAuthReady,
  subscribeAuthState,
  signOutForAccountSwitch,
} from '../services/authService';
import {
  subscribeUserProfile,
  upsertUserProfile,
} from '../services/firestoreNotes';
import type {Entitlement} from '../types/Note';
import posthog, {isPostHogConfigured} from '../posthog';

interface AuthContextValue {
  user: User | null;
  uid: string | null;
  /** Persisted auth state has been restored (user may still be null). */
  ready: boolean;
  entitlement: Entitlement;
  /** Re-read auth.currentUser after link/merge (providerData changes). */
  refreshUser: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  uid: null,
  ready: false,
  entitlement: 'free',
  refreshUser: () => {},
});

export function AuthProvider({children}: {children: ReactNode}) {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const [entitlement, setEntitlement] = useState<Entitlement>('free');
  const [version, setVersion] = useState(0);
  const identifiedUid = useRef<string | null | undefined>(undefined);

  // No anonymous accounts on web: when the restored state is null the app
  // shows the login screen and waits for a Google/Apple sign-in. Anonymous
  // sessions left over from earlier builds are signed out.
  useEffect(() => {
    let cancelled = false;
    waitForAuthReady()
      .then(restored => {
        if (restored?.isAnonymous) {
          return signOutForAccountSwitch();
        }
      })
      .finally(() => {
        if (!cancelled) {
          setReady(true);
        }
      });
    const unsubscribe = subscribeAuthState(nextUser => {
      setUser(nextUser);

      const nextUid = nextUser && !nextUser.isAnonymous ? nextUser.uid : null;
      const previousUid = identifiedUid.current;

      if (isPostHogConfigured && nextUid !== previousUid) {
        if (previousUid) {
          posthog.reset();
        }
        if (nextUser && nextUid) {
          const personProperties: Record<string, string> = {};
          if (nextUser.email) {
            personProperties.email = nextUser.email;
          }
          if (nextUser.displayName) {
            personProperties.name = nextUser.displayName;
          }
          posthog.identify(nextUid, personProperties);
        }
      }

      identifiedUid.current = nextUid;
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  // Record the platform on the profile (mobile does the same via
  // upsertUserProfile) — never touches server-owned fields.
  useEffect(() => {
    if (user?.uid) {
      upsertUserProfile(user.uid, {platform: 'web'}).catch(() => {});
    }
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) {
      setEntitlement('free');
      return;
    }
    return subscribeUserProfile(user.uid, profile =>
      setEntitlement(profile.entitlement),
    );
  }, [user?.uid]);

  // The Firebase User object is mutated in place on link/merge, so bump a
  // version counter to force a fresh context value for consumers.
  const refreshUser = useCallback(() => {
    setUser(auth.currentUser);
    setVersion(v => v + 1);
  }, []);
  void version;

  return (
    <AuthContext.Provider
      value={{
        user,
        uid: user?.uid ?? null,
        ready,
        entitlement,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
